const ENVELOPE_VERSION = 1;
const ENVELOPE_TYPE = 'messenger_v2';
const URL_REGEX = /((?:https?:\/\/|www\.)[^\s<]+)/gi;
const ATTACHMENT_ENCRYPTION_KEY_FIELDS = [
  'keyB64',
  'key_b64',
  'keyBase64',
  'key_base64',
  'mediaKey',
  'media_key',
  'contentKey',
  'content_key',
  'dekB64',
  'dek_b64',
  'dek',
  'key',
];
const ATTACHMENT_ENCRYPTION_IV_FIELDS = [
  'ivB64',
  'iv_b64',
  'ivBase64',
  'iv_base64',
  'nonceB64',
  'nonce_b64',
  'nonce',
  'mediaIv',
  'media_iv',
  'contentIv',
  'content_iv',
  'iv',
];
const ATTACHMENT_ENCRYPTION_ALGORITHM_FIELDS = [
  'algorithm',
  'alg',
  'cipher',
  'cipherName',
  'cipher_name',
];
const ATTACHMENT_OBJECT_KEY_QUERY_KEYS = ['object_key', 'objectKey', 'key'];

const toStringOrNull = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

const normalizeTarget = (target = null) => {
  if (!target || typeof target !== 'object') {
    return null;
  }

  const serverMsgId = toStringOrNull(target.serverMsgId ?? target.server_msg_id ?? null);
  const clientMsgId = toStringOrNull(target.clientMsgId ?? target.client_msg_id ?? null);
  const messageId = toStringOrNull(target.messageId ?? target.id ?? null);

  if (!serverMsgId && !clientMsgId && !messageId) {
    return null;
  }

  return {
    serverMsgId,
    clientMsgId,
    messageId,
  };
};

const normalizeTextLike = (value) => toStringOrNull(value) || '';

const toPositiveIntegerOrNull = (value) => {
  if (value == null || value === '') {
    return null;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  const normalized = Math.trunc(parsed);
  return normalized >= 0 ? normalized : null;
};

const toBooleanOrNull = (value) => {
  if (value === true || value === false) {
    return value;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') return true;
    if (normalized === 'false') return false;
  }
  return null;
};

const normalizePreview = (preview = null) => {
  if (!preview || typeof preview !== 'object') {
    return null;
  }

  const url = toStringOrNull(preview.url ?? preview.href ?? null);
  const siteName = toStringOrNull(
    preview.siteName ?? preview.site_name ?? preview.hostName ?? null,
  );
  const title = toStringOrNull(preview.title ?? preview.siteName ?? preview.site_name ?? null);
  const description = toStringOrNull(preview.description ?? preview.summary ?? null);
  const imageUrl = toStringOrNull(
    preview.imageUrl ?? preview.image_url ?? preview.thumbnailUrl ?? null,
  );
  const faviconUrl = toStringOrNull(preview.faviconUrl ?? preview.favicon_url ?? null);
  const canonicalUrl = toStringOrNull(preview.canonicalUrl ?? preview.canonical_url ?? null);
  const type = toStringOrNull(preview.type ?? preview.kind ?? preview.previewType ?? null);
  const groupId = toStringOrNull(preview.groupId ?? preview.group_id ?? null);
  const groupName = toStringOrNull(preview.groupName ?? preview.group_name ?? null);
  const groupAvatarUrl = toStringOrNull(
    preview.groupAvatarUrl ??
      preview.group_avatar_url ??
      preview.avatarUrl ??
      preview.avatar_url ??
      null,
  );
  const alreadyMember = Boolean(preview.alreadyMember ?? preview.already_member);
  const groupInviteStatus = toStringOrNull(
    preview.groupInviteStatus ?? preview.group_invite_status ?? preview.status ?? null,
  );
  const groupInviteReason = toStringOrNull(
    preview.groupInviteReason ?? preview.group_invite_reason ?? preview.reason ?? null,
  );
  const groupInviteJoinable = toBooleanOrNull(
    preview.groupInviteJoinable ?? preview.group_invite_joinable ?? preview.joinable ?? null,
  );
  const groupInviteExpired = toBooleanOrNull(
    preview.groupInviteExpired ?? preview.group_invite_expired ?? preview.expired ?? null,
  );
  const groupInviteExpiresAt = toStringOrNull(
    preview.groupInviteExpiresAt ??
      preview.group_invite_expires_at ??
      preview.expiresAt ??
      preview.expires_at ??
      null,
  );
  const groupInviteMemberCount = toPositiveIntegerOrNull(
    preview.groupInviteMemberCount ??
      preview.group_invite_member_count ??
      preview.memberCount ??
      preview.member_count ??
      null,
  );
  const groupInviteMaxMembers = toPositiveIntegerOrNull(
    preview.groupInviteMaxMembers ??
      preview.group_invite_max_members ??
      preview.maxMembers ??
      preview.max_members ??
      null,
  );

  if (
    !url &&
    !siteName &&
    !title &&
    !description &&
    !imageUrl &&
    !faviconUrl &&
    !canonicalUrl &&
    !type &&
    !groupId &&
    !groupName &&
    !groupAvatarUrl &&
    !groupInviteStatus
  ) {
    return null;
  }

  return {
    url,
    siteName,
    title,
    description,
    imageUrl,
    faviconUrl,
    canonicalUrl,
    type,
    groupId,
    groupName,
    groupAvatarUrl,
    alreadyMember,
    groupInviteStatus,
    groupInviteReason,
    groupInviteJoinable,
    groupInviteExpired,
    groupInviteExpiresAt,
    groupInviteMemberCount,
    groupInviteMaxMembers,
  };
};

const stripUrlsFromText = (value) => {
  const normalized = toStringOrNull(value);
  if (!normalized) return '';

  return normalized
    .replace(URL_REGEX, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\s+([.,!?;:])/g, '$1')
    .trim();
};

const resolveAttachmentKind = ({ kind = null, mimeType = null, name = null } = {}) => {
  const normalizedKind = toStringOrNull(kind)?.toLowerCase();
  if (normalizedKind) {
    return normalizedKind;
  }

  const normalizedMimeType = toStringOrNull(mimeType)?.toLowerCase() || '';
  if (normalizedMimeType.startsWith('image/')) return 'image';
  if (normalizedMimeType.startsWith('video/')) return 'video';
  if (normalizedMimeType.startsWith('audio/')) return 'audio';
  if (
    normalizedMimeType === 'application/pdf' ||
    normalizedMimeType.includes('document') ||
    normalizedMimeType.includes('msword') ||
    normalizedMimeType.includes('officedocument') ||
    normalizedMimeType.includes('text/')
  ) {
    return 'document';
  }

  const normalizedName = toStringOrNull(name)?.toLowerCase() || '';
  if (
    normalizedName.endsWith('.webp') ||
    normalizedName.endsWith('.png') ||
    normalizedName.endsWith('.jpg') ||
    normalizedName.endsWith('.jpeg') ||
    normalizedName.endsWith('.gif') ||
    normalizedName.endsWith('.avif') ||
    normalizedName.endsWith('.heic') ||
    normalizedName.endsWith('.heif')
  ) {
    return 'image';
  }
  if (
    normalizedName.endsWith('.mp4') ||
    normalizedName.endsWith('.m4v') ||
    normalizedName.endsWith('.webm') ||
    normalizedName.endsWith('.mov') ||
    normalizedName.endsWith('.ogg') ||
    normalizedName.endsWith('.ogv')
  ) {
    return 'video';
  }
  if (
    normalizedName.endsWith('.pdf') ||
    normalizedName.endsWith('.doc') ||
    normalizedName.endsWith('.docx') ||
    normalizedName.endsWith('.txt') ||
    normalizedName.endsWith('.rtf') ||
    normalizedName.endsWith('.xls') ||
    normalizedName.endsWith('.xlsx') ||
    normalizedName.endsWith('.csv') ||
    normalizedName.endsWith('.ppt') ||
    normalizedName.endsWith('.pptx')
  ) {
    return 'document';
  }

  return 'file';
};

const resolveAttachmentKindLabel = (kind = null) => {
  switch (toStringOrNull(kind)?.toLowerCase()) {
    case 'image':
      return 'Image';
    case 'video':
      return 'Video';
    case 'audio':
      return 'Audio';
    case 'document':
      return 'Document';
    default:
      return 'File';
  }
};

const resolveAttachmentField = (attachment = null, keys = []) => {
  if (!attachment || typeof attachment !== 'object' || !Array.isArray(keys)) {
    return null;
  }

  const containers = [
    attachment,
    attachment.metadata,
    attachment.meta,
    attachment.file,
    attachment.blob,
    attachment.asset,
    attachment.attachment,
    attachment.raw,
    attachment.raw?.metadata,
    attachment.raw?.meta,
    attachment.raw?.file,
    attachment.raw?.blob,
    attachment.raw?.asset,
    attachment.raw?.attachment,
  ].filter((container) => container && typeof container === 'object');

  for (const container of containers) {
    for (const key of keys) {
      const value = toStringOrNull(container?.[key]);
      if (value) {
        return value;
      }
    }
  }

  return null;
};

const resolveAttachmentArrayField = (attachment = null, keys = []) => {
  if (!attachment || typeof attachment !== 'object' || !Array.isArray(keys)) return null;
  const containers = [
    attachment,
    attachment.metadata,
    attachment.meta,
    attachment.raw,
    attachment.raw?.metadata,
    attachment.raw?.meta,
  ].filter((container) => container && typeof container === 'object');
  for (const container of containers) {
    for (const key of keys) {
      if (Array.isArray(container[key])) return container[key];
    }
  }
  return null;
};

const extractAttachmentObjectKeyFromUrl = (value = null) => {
  const normalized = toStringOrNull(value);
  if (!normalized) {
    return null;
  }

  try {
    const parsed = new URL(
      normalized,
      typeof window !== 'undefined' ? window.location.origin : undefined,
    );
    for (const key of ATTACHMENT_OBJECT_KEY_QUERY_KEYS) {
      const objectKey = toStringOrNull(parsed.searchParams.get(key));
      if (objectKey) {
        return objectKey;
      }
    }
    const hostname = toStringOrNull(parsed.hostname)?.toLowerCase() || '';
    if (!hostname.endsWith('.cloud.ovh.net') && hostname !== 'cloud.ovh.net') {
      return null;
    }
    const pathSegments = parsed.pathname
      .split('/')
      .map((segment) => segment.trim())
      .filter(Boolean);
    return pathSegments.length >= 2
      ? decodeURIComponent(pathSegments.slice(1).join('/')) || null
      : null;
  } catch {
    return null;
  }
};

const resolveAttachmentEncryption = (attachment = null) => {
  const candidates = [
    attachment?.encryption,
    attachment?.raw?.encryption,
    attachment?.metadata?.encryption,
    attachment?.meta?.encryption,
    attachment?.raw?.metadata?.encryption,
    attachment?.raw?.meta?.encryption,
  ].filter((candidate) => candidate && typeof candidate === 'object');

  const normalized = candidates
    .map((candidate) => {
      const keyB64 = resolveAttachmentField(candidate, ATTACHMENT_ENCRYPTION_KEY_FIELDS);
      const ivB64 = resolveAttachmentField(candidate, ATTACHMENT_ENCRYPTION_IV_FIELDS);
      const algorithm = resolveAttachmentField(candidate, ATTACHMENT_ENCRYPTION_ALGORITHM_FIELDS);
      if (!keyB64 && !ivB64 && !algorithm) {
        return null;
      }
      return {
        ...candidate,
        algorithm: algorithm || candidate.algorithm || candidate.alg || 'AES-GCM',
        keyB64,
        key_b64: keyB64,
        ivB64,
        iv_b64: ivB64,
      };
    })
    .find(Boolean);

  return normalized || candidates[0] || null;
};

const normalizeAttachment = (attachment = null) => {
  if (!attachment || typeof attachment !== 'object') {
    return null;
  }

  const id = resolveAttachmentField(attachment, ['id', 'attachmentId', 'attachment_id']);
  const url = resolveAttachmentField(attachment, [
    'url',
    'previewUrl',
    'preview_url',
    'fileUrl',
    'file_url',
    'downloadUrl',
    'download_url',
    'sourceUrl',
    'source_url',
    'mediaUrl',
    'media_url',
    'proxyUrl',
    'proxy_url',
    'originalUrl',
    'original_url',
    'attachmentUrl',
    'attachment_url',
    'src',
  ]);
  const objectKey =
    resolveAttachmentField(attachment, [
      'objectKey',
      'object_key',
      'storageKey',
      'storage_key',
      'blobKey',
      'blob_key',
      'fileKey',
      'file_key',
      'assetKey',
      'asset_key',
      'objectName',
      'object_name',
      'key',
      'object',
    ]) || extractAttachmentObjectKeyFromUrl(url);
  const fileUrl = resolveAttachmentField(attachment, [
    'fileUrl',
    'file_url',
    'sourceUrl',
    'source_url',
    'mediaUrl',
    'media_url',
    'originalUrl',
    'original_url',
    'attachmentUrl',
    'attachment_url',
  ]);
  const downloadUrl = resolveAttachmentField(attachment, [
    'downloadUrl',
    'download_url',
    'proxyUrl',
    'proxy_url',
    'src',
  ]);
  const thumbnailUrl = resolveAttachmentField(attachment, [
    'thumbnailUrl',
    'thumbnail_url',
    'thumbUrl',
    'thumb_url',
  ]);
  const posterUrl = resolveAttachmentField(attachment, ['posterUrl', 'poster_url', 'poster']);
  const previewImageUrl = resolveAttachmentField(attachment, [
    'previewImageUrl',
    'preview_image_url',
    'previewImage',
    'preview_image',
    'previewUrl',
    'preview_url',
  ]);
  const name = resolveAttachmentField(attachment, [
    'name',
    'filename',
    'fileName',
    'file_name',
    'originalFilename',
    'original_filename',
  ]);
  const mimeType = resolveAttachmentField(attachment, [
    'mimeType',
    'mime_type',
    'type',
    'contentType',
    'content_type',
  ]);
  const rawKind = resolveAttachmentField(attachment, ['kind', 'fileKind', 'file_kind']);
  const kind = resolveAttachmentKind({
    kind: rawKind,
    mimeType,
    name,
  });
  const caption = resolveAttachmentField(attachment, ['caption']);
  const size = toPositiveIntegerOrNull(
    resolveAttachmentField(attachment, ['size', 'byteSize', 'byte_size', 'fileSize', 'file_size']),
  );
  const width = toPositiveIntegerOrNull(resolveAttachmentField(attachment, ['width']));
  const height = toPositiveIntegerOrNull(resolveAttachmentField(attachment, ['height']));
  const durationMs = toPositiveIntegerOrNull(
    resolveAttachmentField(attachment, ['durationMs', 'duration_ms', 'duration']),
  );
  const voiceNote = toBooleanOrNull(
    resolveAttachmentField(attachment, ['voiceNote', 'voice_note', 'isVoiceNote']),
  );
  const waveformSource = resolveAttachmentArrayField(attachment, [
    'waveform',
    'voiceWaveform',
    'voice_waveform',
  ]);
  const waveform = waveformSource
    ? waveformSource
        .map((value) => Number(value))
        .filter(Number.isFinite)
        .slice(0, 128)
        .map((value) => Math.max(0.04, Math.min(1, value)))
    : null;
  const encryptionSource = resolveAttachmentEncryption(attachment);
  const encryption = encryptionSource
    ? {
        algorithm: toStringOrNull(encryptionSource.algorithm ?? encryptionSource.alg ?? null),
        keyB64: toStringOrNull(encryptionSource.keyB64 ?? encryptionSource.key_b64 ?? null),
        ivB64: toStringOrNull(encryptionSource.ivB64 ?? encryptionSource.iv_b64 ?? null),
      }
    : null;
  const raw =
    attachment.raw && typeof attachment.raw === 'object' ? attachment.raw : { ...attachment };

  if (
    !id &&
    !objectKey &&
    !url &&
    !fileUrl &&
    !downloadUrl &&
    !thumbnailUrl &&
    !posterUrl &&
    !previewImageUrl &&
    !name &&
    !mimeType &&
    !rawKind &&
    !caption &&
    size == null &&
    width == null &&
    height == null &&
    durationMs == null &&
    voiceNote == null &&
    !waveform?.length &&
    !encryption
  ) {
    return null;
  }

  return {
    id,
    objectKey,
    object_key: objectKey,
    url,
    fileUrl,
    file_url: fileUrl,
    downloadUrl,
    download_url: downloadUrl,
    thumbnailUrl,
    thumbnail_url: thumbnailUrl,
    posterUrl,
    poster_url: posterUrl,
    previewImageUrl,
    preview_image_url: previewImageUrl,
    name,
    mimeType,
    mime_type: mimeType,
    kind,
    caption,
    size,
    width,
    height,
    durationMs,
    duration_ms: durationMs,
    voiceNote,
    voice_note: voiceNote,
    waveform,
    encryption,
    raw,
  };
};

const normalizeQuote = (quote = null) => {
  if (!quote || typeof quote !== 'object') {
    return null;
  }

  const messageId = toStringOrNull(quote.messageId ?? quote.message_id ?? quote.id ?? null);
  const serverMsgId = toStringOrNull(quote.serverMsgId ?? quote.server_msg_id ?? null);
  const clientMsgId = toStringOrNull(quote.clientMsgId ?? quote.client_msg_id ?? null);
  const authorId = toStringOrNull(quote.authorId ?? quote.author_id ?? null);
  const authorName = toStringOrNull(quote.authorName ?? quote.author_name ?? null);
  const body = toStringOrNull(quote.body ?? quote.text ?? null);
  const createdAt = toStringOrNull(quote.createdAt ?? quote.created_at ?? null);
  const preview = normalizePreview(
    quote.preview ?? quote.linkPreview ?? quote.link_preview ?? null,
  );
  const messageKind = toStringOrNull(quote.messageKind ?? quote.message_kind ?? quote.kind ?? null);
  const rawAttachmentIndex = Number(
    quote.attachmentIndex ?? quote.attachment_index ?? quote.mediaIndex ?? quote.media_index ?? NaN,
  );
  const attachmentIndex =
    Number.isFinite(rawAttachmentIndex) && rawAttachmentIndex >= 0
      ? Math.trunc(rawAttachmentIndex)
      : null;
  const attachments = Array.isArray(quote.attachments)
    ? quote.attachments.map((attachment) => normalizeAttachment(attachment)).filter(Boolean)
    : [];

  if (
    !messageId &&
    !serverMsgId &&
    !clientMsgId &&
    !authorId &&
    !authorName &&
    !body &&
    !createdAt &&
    !preview &&
    !messageKind &&
    attachmentIndex == null &&
    attachments.length === 0
  ) {
    return null;
  }

  return {
    messageId,
    serverMsgId,
    clientMsgId,
    authorId,
    authorName,
    body,
    createdAt,
    preview,
    messageKind,
    attachmentIndex,
    attachments,
  };
};

const normalizeReaction = (reaction = null) => {
  if (!reaction || typeof reaction !== 'object') {
    return null;
  }

  const type = toStringOrNull(
    reaction.type ?? reaction.reactionType ?? reaction.reaction_type ?? null,
  );
  const emoji = toStringOrNull(reaction.emoji ?? reaction.icon ?? null);
  const action = toStringOrNull(reaction.action ?? null) || 'add';

  if (!type && !emoji && action === 'add') {
    return null;
  }

  return {
    type,
    emoji,
    action,
  };
};

const normalizeCallEventParticipant = (participant = null) => {
  if (!participant || typeof participant !== 'object') {
    return null;
  }

  const userId = toStringOrNull(
    participant.userId ??
      participant.user_id ??
      participant.user?.id ??
      participant.user?.userId ??
      participant.user?.user_id ??
      null,
  );
  const identity = toStringOrNull(participant.identity ?? participant.sid ?? null);
  const id = toStringOrNull(
    participant.id ?? userId ?? identity ?? participant.memberId ?? participant.member_id ?? null,
  );
  const name = toStringOrNull(
    participant.name ??
      participant.displayName ??
      participant.display_name ??
      participant.fullName ??
      participant.full_name ??
      participant.username ??
      participant.userName ??
      participant.user_name ??
      participant.user?.name ??
      participant.user?.fullName ??
      participant.user?.full_name ??
      participant.user?.username ??
      null,
  );
  const avatarUrl = toStringOrNull(
    participant.avatarUrl ??
      participant.avatar_url ??
      participant.avatar ??
      participant.displayPicture ??
      participant.display_picture ??
      participant.imageUrl ??
      participant.image_url ??
      participant.user?.avatarUrl ??
      participant.user?.avatar_url ??
      participant.user?.avatar ??
      participant.user?.displayPicture ??
      participant.user?.display_picture ??
      participant.user?.imageUrl ??
      participant.user?.image_url ??
      null,
  );
  const initials = toStringOrNull(
    participant.initials ??
      participant.avatarInitials ??
      participant.avatar_initials ??
      participant.user?.initials ??
      participant.user?.avatarInitials ??
      participant.user?.avatar_initials ??
      null,
  );

  if (!id && !name && !avatarUrl && !initials) {
    return null;
  }

  return {
    id,
    userId,
    identity,
    name,
    avatarUrl,
    initials,
    isLocal: participant.isLocal === true || participant.is_local === true,
  };
};

const normalizeCallEventParticipants = (participants = null) => {
  if (!Array.isArray(participants)) {
    return [];
  }

  const seen = new Set();
  return participants
    .map((participant) => normalizeCallEventParticipant(participant))
    .filter((participant) => {
      if (!participant) return false;
      const key = (
        participant.id ||
        participant.name ||
        participant.avatarUrl ||
        participant.initials ||
        ''
      ).toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 24);
};

const normalizeCallEvent = (callEvent = null) => {
  if (!callEvent || typeof callEvent !== 'object') {
    return null;
  }

  const callId = toStringOrNull(callEvent.callId ?? callEvent.call_id ?? null);
  const status = toStringOrNull(callEvent.status ?? callEvent.state ?? null) || 'ended';
  const mode =
    toStringOrNull(callEvent.mode ?? callEvent.callMode ?? callEvent.call_mode ?? null) === 'video'
      ? 'video'
      : 'voice';
  const direction = toStringOrNull(callEvent.direction ?? null);
  const durationMs = toPositiveIntegerOrNull(
    callEvent.durationMs ?? callEvent.duration_ms ?? callEvent.duration ?? null,
  );
  const startedAt = toStringOrNull(callEvent.startedAt ?? callEvent.started_at ?? null);
  const endedAt = toStringOrNull(callEvent.endedAt ?? callEvent.ended_at ?? null);
  const startedByUserId = toStringOrNull(
    callEvent.startedByUserId ??
      callEvent.started_by_user_id ??
      callEvent.initiatorUserId ??
      callEvent.initiator_user_id ??
      callEvent.initiator?.id ??
      null,
  );
  const startedByName = toStringOrNull(
    callEvent.startedByName ??
      callEvent.started_by_name ??
      callEvent.initiatorName ??
      callEvent.initiator_name ??
      callEvent.initiator?.name ??
      callEvent.initiator?.fullName ??
      callEvent.initiator?.full_name ??
      null,
  );
  const startedByAvatarUrl = toStringOrNull(
    callEvent.startedByAvatarUrl ??
      callEvent.started_by_avatar_url ??
      callEvent.initiatorAvatarUrl ??
      callEvent.initiator_avatar_url ??
      callEvent.initiator?.avatarUrl ??
      callEvent.initiator?.avatar_url ??
      callEvent.initiator?.avatar ??
      callEvent.initiator?.displayPicture ??
      callEvent.initiator?.display_picture ??
      callEvent.initiator?.imageUrl ??
      callEvent.initiator?.image_url ??
      null,
  );
  const startedByInitials = toStringOrNull(
    callEvent.startedByInitials ??
      callEvent.started_by_initials ??
      callEvent.initiatorInitials ??
      callEvent.initiator_initials ??
      callEvent.initiator?.initials ??
      callEvent.initiator?.avatarInitials ??
      callEvent.initiator?.avatar_initials ??
      null,
  );
  const participantCount = toPositiveIntegerOrNull(
    callEvent.participantCount ??
      callEvent.participant_count ??
      callEvent.joinedParticipantCount ??
      callEvent.joined_participant_count ??
      null,
  );
  const participants = normalizeCallEventParticipants(
    callEvent.participants ??
      callEvent.participantSnapshots ??
      callEvent.participant_snapshots ??
      callEvent.members ??
      null,
  );
  const groupCall =
    callEvent.groupCall === true ||
    callEvent.group_call === true ||
    callEvent.isGroupCall === true ||
    callEvent.is_group_call === true ||
    participantCount > 2 ||
    participants.length > 2;

  if (
    !callId &&
    !status &&
    !direction &&
    durationMs == null &&
    !startedAt &&
    !endedAt &&
    !startedByUserId &&
    !startedByName &&
    !startedByAvatarUrl &&
    !startedByInitials &&
    participantCount == null &&
    participants.length === 0 &&
    !groupCall
  ) {
    return null;
  }

  return {
    callId,
    status,
    mode,
    direction,
    durationMs,
    startedAt,
    endedAt,
    startedByUserId,
    startedByName,
    startedByAvatarUrl,
    startedByInitials,
    participantCount,
    participants,
    groupCall,
  };
};

const normalizeCallSignal = (callSignal = null) => {
  if (!callSignal || typeof callSignal !== 'object') {
    return null;
  }

  const callId = toStringOrNull(callSignal.callId ?? callSignal.call_id ?? null);
  const conversationId = toStringOrNull(
    callSignal.conversationId ?? callSignal.conversation_id ?? null,
  );
  const signalType = toStringOrNull(
    callSignal.signalType ?? callSignal.signal_type ?? callSignal.type ?? null,
  )?.toLowerCase();
  const fromUserId = toStringOrNull(callSignal.fromUserId ?? callSignal.from_user_id ?? null);
  const fromClientId = toStringOrNull(callSignal.fromClientId ?? callSignal.from_client_id ?? null);
  const lifecycleVersionValue = Number(
    callSignal.lifecycleVersion ??
      callSignal.lifecycle_version ??
      callSignal.stateVersion ??
      callSignal.state_version ??
      null,
  );
  const lifecycleVersion =
    Number.isFinite(lifecycleVersionValue) && lifecycleVersionValue >= 0
      ? Math.trunc(lifecycleVersionValue)
      : null;
  const payload =
    callSignal.payload && typeof callSignal.payload === 'object' ? callSignal.payload : {};
  const at = toStringOrNull(callSignal.at ?? callSignal.createdAt ?? callSignal.created_at ?? null);

  if (!callId || !conversationId || !signalType) {
    return null;
  }

  return {
    callId,
    conversationId,
    signalType,
    fromUserId,
    fromClientId,
    lifecycleVersion,
    payload,
    at,
    pqE2ee: true,
  };
};

const normalizeAttachments = (attachments = null) => {
  if (!Array.isArray(attachments)) {
    return [];
  }
  return attachments.map((attachment) => normalizeAttachment(attachment)).filter(Boolean);
};

const normalizeForwarded = (forwarded = null) => {
  if (forwarded === true) {
    return { version: 1, originalKind: null, note: null };
  }
  if (!forwarded || typeof forwarded !== 'object') return null;
  return {
    version: Math.max(1, Math.trunc(Number(forwarded.version) || 1)),
    originalKind: toStringOrNull(forwarded.originalKind ?? forwarded.original_kind ?? null),
    note: toStringOrNull(forwarded.note ?? null),
  };
};

export const createMessengerV2TextEnvelope = (text) => ({
  version: ENVELOPE_VERSION,
  type: ENVELOPE_TYPE,
  kind: 'text',
  text: toStringOrNull(text) || '',
});

export const createMessengerV2EditEnvelope = ({ target, text, preview } = {}) => ({
  version: ENVELOPE_VERSION,
  type: ENVELOPE_TYPE,
  kind: 'edit',
  target: normalizeTarget(target),
  text: toStringOrNull(text) || '',
  preview: normalizePreview(preview),
});

export const createMessengerV2DeleteEnvelope = ({ target } = {}) => ({
  version: ENVELOPE_VERSION,
  type: ENVELOPE_TYPE,
  kind: 'delete',
  target: normalizeTarget(target),
});

export const createMessengerV2ReplyEnvelope = ({ target, text, quote, preview } = {}) => ({
  version: ENVELOPE_VERSION,
  type: ENVELOPE_TYPE,
  kind: 'reply',
  target: normalizeTarget(target),
  text: normalizeTextLike(text),
  quote: normalizeQuote(quote),
  preview: normalizePreview(preview),
});

export const createMessengerV2ReactionEnvelope = ({ target, reaction, emoji, action, quote, fallbackText } = {}) => ({
  version: ENVELOPE_VERSION,
  type: ENVELOPE_TYPE,
  kind: 'reaction',
  target: normalizeTarget(target),
  text: normalizeTextLike(fallbackText),
  quote: normalizeQuote(quote),
  reaction: normalizeReaction(
    typeof reaction === 'object'
      ? reaction
      : {
          type: reaction,
          emoji,
          action,
        },
  ),
});

export const createMessengerV2AttachmentEnvelope = ({
  target,
  attachments,
  caption,
  preview,
  quote,
} = {}) => ({
  version: ENVELOPE_VERSION,
  type: ENVELOPE_TYPE,
  kind: 'attachment',
  target: normalizeTarget(target),
  attachments: normalizeAttachments(attachments),
  caption: normalizeTextLike(caption),
  preview: normalizePreview(preview),
  quote: normalizeQuote(quote),
});

export const createMessengerV2LinkPreviewEnvelope = ({ target, text, preview, quote } = {}) => ({
  version: ENVELOPE_VERSION,
  type: ENVELOPE_TYPE,
  kind: 'link_preview',
  target: normalizeTarget(target),
  text: normalizeTextLike(text),
  preview: normalizePreview(preview),
  quote: normalizeQuote(quote),
});

export const createMessengerV2CallEventEnvelope = ({ callEvent, text } = {}) => ({
  version: ENVELOPE_VERSION,
  type: ENVELOPE_TYPE,
  kind: 'call_event',
  text: normalizeTextLike(text),
  callEvent: normalizeCallEvent(callEvent),
});

export const createMessengerV2CallSignalEnvelope = ({ callSignal } = {}) => ({
  version: ENVELOPE_VERSION,
  type: ENVELOPE_TYPE,
  kind: 'call_signal',
  hidden: true,
  text: '',
  callSignal: normalizeCallSignal(callSignal),
});

export const serializeMessengerV2Envelope = (envelope = null) =>
  JSON.stringify(envelope && typeof envelope === 'object' ? envelope : {});

export const parseMessengerV2Envelope = (plaintext = null) => {
  const normalizedPlaintext = toStringOrNull(plaintext);
  if (!normalizedPlaintext) {
    return {
      kind: 'text',
      text: '',
      target: null,
      quote: null,
      reaction: null,
      callEvent: null,
      callSignal: null,
      forwarded: null,
      attachments: [],
      preview: null,
      hidden: false,
      raw: '',
    };
  }

  try {
    const parsed = JSON.parse(normalizedPlaintext);
    if (
      parsed &&
      typeof parsed === 'object' &&
      parsed.type === ENVELOPE_TYPE &&
      Number(parsed.version) === ENVELOPE_VERSION
    ) {
      const kind = toStringOrNull(parsed.kind) || 'text';
      return {
        kind,
        text: toStringOrNull(parsed.text) || '',
        target: normalizeTarget(parsed.target),
        quote: normalizeQuote(parsed.quote ?? parsed.reply ?? null),
        reaction: normalizeReaction(parsed.reaction ?? null),
        callEvent: normalizeCallEvent(parsed.callEvent ?? parsed.call_event ?? null),
        callSignal: normalizeCallSignal(parsed.callSignal ?? parsed.call_signal ?? null),
        forwarded: normalizeForwarded(parsed.forwarded ?? null),
        attachments: normalizeAttachments(parsed.attachments ?? parsed.files ?? null),
        caption: toStringOrNull(parsed.caption ?? parsed.text ?? null),
        preview: normalizePreview(
          parsed.preview ?? parsed.linkPreview ?? parsed.link_preview ?? null,
        ),
        hidden: parsed.hidden === true || kind === 'call_signal',
        raw: normalizedPlaintext,
      };
    }
  } catch {}

  return {
    kind: 'text',
    text: normalizedPlaintext,
    target: null,
    quote: null,
    reaction: null,
    callEvent: null,
    callSignal: null,
    forwarded: null,
    attachments: [],
    caption: null,
    preview: null,
    hidden: false,
    raw: normalizedPlaintext,
  };
};

export const isMessengerV2CallMessage = (item = null) =>
  item?.messageKind === 'call_event' || item?.messageKind === 'call_signal' || Boolean(item?.callEvent || item?.callSignal);

export const resolveMessengerV2EnvelopeDisplayText = (envelope = null) => {
  if (!envelope || typeof envelope !== 'object') {
    return '';
  }

  if (envelope.kind === 'text' || envelope.kind === 'edit' || envelope.kind === 'reply') {
    return toStringOrNull(envelope.text) || '';
  }

  if (envelope.kind === 'attachment') {
    return (
      toStringOrNull(envelope.caption) ||
      resolveMessengerV2AttachmentSummaryText(envelope.attachments) ||
      ''
    );
  }

  if (envelope.kind === 'link_preview') {
    return stripUrlsFromText(envelope.text);
  }

  if (envelope.kind === 'call_event') {
    return toStringOrNull(envelope.text) || resolveMessengerV2CallEventText(envelope.callEvent);
  }

  if (envelope.kind === 'call_signal' || envelope.hidden === true) {
    return '';
  }

  return '';
};

export const resolveMessengerV2NotificationPreviewText = (envelope = null, maxLength = 160) => {
  if (!envelope || typeof envelope !== 'object' || envelope.hidden === true) {
    return '';
  }

  let previewText = '';
  if (envelope.kind === 'attachment') {
    const firstAttachment = Array.isArray(envelope.attachments)
      ? normalizeAttachment(envelope.attachments[0])
      : null;
    previewText =
      toStringOrNull(envelope.caption) ||
      toStringOrNull(firstAttachment?.caption) ||
      resolveMessengerV2NotificationAttachmentText(envelope.attachments) ||
      '';
  } else if (envelope.kind === 'reaction') {
    const emoji = toStringOrNull(envelope.reaction?.emoji ?? envelope.reaction?.type ?? null);
    const target = toStringOrNull(envelope.quote?.body) ||
      resolveMessengerV2NotificationAttachmentText(envelope.quote?.attachments) || '';
    previewText = envelope.reaction?.action === 'remove'
      ? toStringOrNull(envelope.text) || ''
      : target && emoji ? `Reacted ${emoji} to “${target}”`
        : emoji ? `Reacted ${emoji}` : 'Reacted to a message';
  } else {
    previewText = resolveMessengerV2EnvelopeDisplayText(envelope);
  }

  const normalized = String(previewText || '')
    .replace(/\s+/g, ' ')
    .trim();
  const limit = Math.max(1, Number(maxLength) || 160);
  if (normalized.length <= limit) return normalized;
  return `${normalized.slice(0, Math.max(1, limit - 3))}...`;
};

export const resolveMessengerV2CallEventText = (callEvent = null) => {
  const status = toStringOrNull(callEvent?.status ?? null)?.toLowerCase();
  const mode =
    toStringOrNull(callEvent?.mode ?? callEvent?.callMode ?? null) === 'video' ? 'video' : 'voice';
  const mediaLabel = mode === 'video' ? 'Video call' : 'Voice call';
  switch (status) {
    case 'missed':
      return mode === 'video' ? 'Missed video call' : 'Missed voice call';
    case 'declined':
      return mode === 'video' ? 'Video call declined' : 'Voice call declined';
    case 'cancelled':
      return mode === 'video' ? 'Video call cancelled' : 'Voice call cancelled';
    case 'failed':
      return mode === 'video' ? 'Video call failed' : 'Voice call failed';
    default:
      return mediaLabel;
  }
};

export const resolveMessengerV2AttachmentSummaryText = (attachments = []) => {
  const normalizedAttachments = normalizeAttachments(attachments);
  if (!normalizedAttachments.length) {
    return '';
  }

  const firstAttachment = normalizedAttachments[0];
  const voiceNoteCount = normalizedAttachments.filter(
    (attachment) => attachment.voiceNote === true || attachment.voice_note === true,
  ).length;
  if (voiceNoteCount === normalizedAttachments.length) {
    return voiceNoteCount === 1 ? 'Voice message' : `${voiceNoteCount} Voice messages`;
  }
  const primaryLabel =
    toStringOrNull(firstAttachment.name ?? null) ||
    resolveAttachmentKindLabel(firstAttachment.kind) ||
    'Attachment';

  if (normalizedAttachments.length === 1) {
    return primaryLabel;
  }

  return `${primaryLabel} + ${normalizedAttachments.length - 1} more`;
};

export const resolveMessengerV2NotificationAttachmentText = (attachments = []) => {
  const normalizedAttachments = normalizeAttachments(attachments);
  if (!normalizedAttachments.length) return '';

  if (normalizedAttachments.length === 1) {
    const attachment = normalizedAttachments[0];
    switch (attachment.kind) {
      case 'image':
        return 'Photo';
      case 'video':
        return 'Video';
      case 'audio':
        return 'Voice note';
      case 'document':
      case 'file':
      default:
        return (
          toStringOrNull(attachment.name ?? null) ||
          resolveAttachmentKindLabel(attachment.kind) ||
          'Attachment'
        );
    }
  }

  const attachmentKinds = new Set(normalizedAttachments.map((attachment) => attachment.kind));
  if (attachmentKinds.size === 1) {
    const [kind] = attachmentKinds;
    if (kind === 'image') return `${normalizedAttachments.length} Photos`;
    if (kind === 'video') return `${normalizedAttachments.length} Videos`;
    if (kind === 'audio') return `${normalizedAttachments.length} Voice notes`;
  }
  return `${normalizedAttachments.length} attachments`;
};

export const resolveMessengerV2EnvelopeTarget = normalizeTarget;
export const resolveMessengerV2AttachmentKind = resolveAttachmentKind;
